import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import type { ReactElement, ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { invalidationGroups, queryKeys } from '../api/query-keys';
import type { LiveStreamState } from '../domain/labels';

/**
 * Live updates.
 *
 * The stream is read-only by design (see `apps/api/src/http/sse.ts`): a frame
 * only invalidates react-query caches, it never carries a value that is
 * rendered directly and never confirms a command. If the stream drops, the
 * panel degrades to polling and *says so* in the header — a silently stale
 * trading dashboard is worse than an obviously degraded one.
 */

export const EVENTS_URL = '/api/events';

const POLL_INTERVAL_MS = 10_000;
const RECONNECT_BASE_MS = 3_000;
const RECONNECT_MAX_MS = 60_000;

export interface LiveEventsValue {
  readonly state: LiveStreamState;
  /** When the last frame (including heartbeats) arrived. */
  readonly lastEventAt: string | null;
  readonly reconnect: () => void;
}

const FALLBACK: LiveEventsValue = {
  state: 'polling',
  lastEventAt: null,
  reconnect: () => undefined,
};

const LiveEventsContext = createContext<LiveEventsValue>(FALLBACK);

type EventGroupName = keyof typeof invalidationGroups;

const EVENT_GROUP_NAMES = Object.keys(invalidationGroups) as EventGroupName[];

const POLLED_KEYS: readonly (readonly unknown[])[] = [
  queryKeys.dashboard,
  queryKeys.position,
  queryKeys.orders,
  queryKeys.health,
  ['decisions'],
];

function invalidate(client: QueryClient, keys: readonly (readonly unknown[])[]): void {
  for (const key of keys) {
    void client.invalidateQueries({ queryKey: key });
  }
}

export function LiveEventsProvider({ children }: { children: ReactNode }): ReactElement {
  const queryClient = useQueryClient();
  const [state, setState] = useState<LiveStreamState>('connecting');
  const [lastEventAt, setLastEventAt] = useState<string | null>(null);
  const [attempt, setAttempt] = useState(0);
  const attemptRef = useRef(0);

  const reconnect = useCallback(() => {
    attemptRef.current = 0;
    setAttempt((value) => value + 1);
  }, []);

  useEffect(() => {
    // jsdom and any environment without SSE support goes straight to polling
    // rather than crashing on `new EventSource`.
    if (typeof EventSource === 'undefined') {
      setState('polling');
      return undefined;
    }

    setState('connecting');
    const source = new EventSource(EVENTS_URL);
    let reconnectTimer: ReturnType<typeof setTimeout> | null = null;
    let closed = false;

    const markSeen = (): void => {
      setLastEventAt(new Date().toISOString());
    };

    source.onopen = () => {
      attemptRef.current = 0;
      setState('live');
      markSeen();
    };

    const listeners = EVENT_GROUP_NAMES.map((name) => {
      const listener = (): void => {
        markSeen();
        invalidate(queryClient, invalidationGroups[name]);
      };
      source.addEventListener(name, listener);
      return [name, listener] as const;
    });

    const heartbeat = (): void => {
      markSeen();
    };
    source.addEventListener('heartbeat', heartbeat);

    source.onerror = () => {
      if (closed) return;
      closed = true;
      source.close();
      setState('polling');
      attemptRef.current += 1;
      const delay = Math.min(RECONNECT_BASE_MS * 2 ** (attemptRef.current - 1), RECONNECT_MAX_MS);
      reconnectTimer = setTimeout(() => {
        setAttempt((value) => value + 1);
      }, delay);
    };

    return () => {
      closed = true;
      if (reconnectTimer !== null) clearTimeout(reconnectTimer);
      for (const [name, listener] of listeners) source.removeEventListener(name, listener);
      source.removeEventListener('heartbeat', heartbeat);
      source.close();
    };
  }, [queryClient, attempt]);

  // Degraded mode: keep the numbers moving even without a stream.
  useEffect(() => {
    if (state !== 'polling') return undefined;
    const timer = setInterval(() => {
      invalidate(queryClient, POLLED_KEYS);
    }, POLL_INTERVAL_MS);
    return () => {
      clearInterval(timer);
    };
  }, [state, queryClient]);

  const value = useMemo<LiveEventsValue>(
    () => ({ state, lastEventAt, reconnect }),
    [state, lastEventAt, reconnect],
  );

  return <LiveEventsContext.Provider value={value}>{children}</LiveEventsContext.Provider>;
}

export function useLiveEvents(): LiveEventsValue {
  return useContext(LiveEventsContext);
}
